import { useState, type ReactNode } from "react";
import { Button, Dialog } from "./primitives";

/**
 * A button that asks for confirmation before running a (usually destructive)
 * action. Used everywhere a delete/remove happens, so nothing is ever deleted
 * on a single click.
 */
export function ConfirmButton({
  onConfirm,
  children,
  message,
  title = "Please confirm",
  confirmLabel = "Delete",
  buttonVariant = "ghost",
  className,
  disabled,
  ...rest
}: {
  onConfirm: () => void | Promise<void>;
  children: ReactNode;
  message: ReactNode;
  title?: string;
  confirmLabel?: string;
  buttonVariant?: "default" | "outline" | "ghost" | "destructive";
  className?: string;
  disabled?: boolean;
  "aria-label"?: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant={buttonVariant}
        className={className}
        disabled={disabled}
        onClick={() => setOpen(true)}
        {...rest}
      >
        {children}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={title}>
        <div className="text-sm text-muted-foreground">{message}</div>
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
                setOpen(false);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </div>
      </Dialog>
    </>
  );
}
